import { Controller } from "@hotwired/stimulus"

// Animated disclosure for the apex AI Resume Translator FAQ.
//
// <details> has no transition of its own: the browser flips `open` and the
// answer appears in the SAME frame — a jump-cut sitting directly under a page
// whose every other reveal eases. The only way to animate it is to drive the
// panel's `max-height` from its own `scrollHeight`, and that means taking the
// toggle away from the browser: the summary's click is prevented and `open` is
// set by hand — LATE on close, so the closing frames still have something to
// paint. (Set `open = false` first and the content is gone before the
// animation starts; the panel would collapse instantly and the transition
// would run on an empty box.)
//
// The hidden state lives in CSS as a (0,1,0) `max-height: 0` that
// `.airt-faq__item[open] .airt-faq__a` overrides at (0,2,0). That ordering is
// load-bearing, the same call `.is-reveal-ready` makes for the scroll reveal:
// with JS off, or before this controller connects, an open <details> sits at
// `max-height: none` and reads normally. The failure mode here is "no
// animation", never "an open answer clipped to zero height".
const DURATION_MS = 380 // tracks the .airt-faq__a max-height transition in the SCSS

export default class extends Controller {
  static targets = ["item"]

  connect() {
    // Under reduced motion the animated path is skipped ENTIRELY rather than
    // run at the 0.001ms duration the page's global exemption forces. A
    // zero-length transition is not guaranteed to fire `transitionend`, and a
    // close that never settles leaves `open` true with the panel pinned at
    // max-height 0 — the answer unreachable, with nothing throwing.
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.pending = new Map()
    // Server-rendered `open` items start with no inline ceiling; CSS already
    // has them at `max-height: none`. Only aria needs seeding.
    this.itemTargets.forEach((d) => this.syncAria(d))
  }

  // Turbo caches and restores pages. A pending close would otherwise fire on a
  // detached element and flip `open` on the restored copy.
  disconnect() {
    this.pending.forEach((abort) => abort())
    this.pending.clear()
  }

  toggle(event) {
    // Covers the keyboard too: <summary> turns Enter and Space into a click,
    // so preventing the click is what stops the native toggle in every input
    // mode, and this handler is the only thing that ever sets `open`.
    event.preventDefault()
    const details = event.currentTarget.closest("details")
    if (!details) return
    if (details.open) this.close(details); else this.open(details)
  }

  open(details) {
    const panel = this.panel(details)
    details.open = true
    this.syncAria(details)
    if (!panel) return
    this.abort(details)
    if (this.reduced) { panel.style.maxHeight = ""; return }

    panel.style.maxHeight = "0px"
    this.reflow(panel)
    panel.style.maxHeight = `${panel.scrollHeight}px`
    // Hand the panel back to CSS once it has arrived, so a later reflow (font
    // swap, resize, zoom, a longer answer) is never clipped by a stale ceiling.
    this.after(details, panel, () => { panel.style.maxHeight = "" })
  }

  close(details) {
    const panel = this.panel(details)
    this.abort(details)
    if (!panel || this.reduced) {
      details.open = false
      this.syncAria(details)
      if (panel) panel.style.maxHeight = ""
      return
    }

    // From `none` to a concrete height first — `max-height: none` is not
    // interpolable, so animating straight to 0 from it is an instant snap.
    panel.style.maxHeight = `${panel.scrollHeight}px`
    this.reflow(panel)
    panel.style.maxHeight = "0px"
    this.after(details, panel, () => {
      details.open = false
      this.syncAria(details)
      panel.style.maxHeight = ""
    })
  }

  // `aria-expanded` is DERIVED from `details.open` and written in the same
  // statement pair every time it changes, so the two can never disagree —
  // including mid-close, where `open` is deliberately still true.
  syncAria(details) {
    const summary = details.querySelector("summary")
    if (summary) summary.setAttribute("aria-expanded", String(details.open))
  }

  panel(details) {
    return details.querySelector(".airt-faq__a")
  }

  // Forces style/layout to flush so the starting value is committed as its own
  // frame. Without it both assignments coalesce into one computed value and
  // there is no transition to run — the exact jump-cut this file exists to fix.
  reflow(el) {
    void el.scrollHeight
  }

  after(details, panel, fn) {
    this.abort(details)
    let settled = false
    const stop = () => {
      settled = true
      panel.removeEventListener("transitionend", onEnd)
      clearTimeout(timer)
      this.pending.delete(details)
    }
    const finish = () => { if (!settled) { stop(); fn() } }
    const onEnd = (e) => { if (e.target === panel && e.propertyName === "max-height") finish() }

    panel.addEventListener("transitionend", onEnd)
    // Safety net, not belt-and-braces. This page ships inside the full
    // unpurged marketing bundle, which already carries 27 unscoped
    // `animation: none !important` declarations; a `transition: none` twin
    // landing in it would mean `transitionend` never fires and a closing panel
    // stayed at max-height 0 with `open` still true — the answer gone, no error.
    const timer = setTimeout(finish, DURATION_MS + 120)
    this.pending.set(details, () => { if (!settled) stop() })
  }

  abort(details) {
    const cancel = this.pending.get(details)
    if (cancel) cancel()
  }
}
